import Vue from 'vue'
import Axios from 'axios'

const convertToFormData = (params) => {
  let form_data = new FormData
  for (var key in params) {
    if (typeof params[key] === 'object' && params[key].length !== undefined) {
      for (let i = 0; i < params[key].length; i++) {
        form_data.append(key+'[]', params[key][i])
      }
    } else {
      form_data.append(key, params[key])
    }
  }

  return form_data
}

export const ApiServiceInit = (store) => {
  Vue.prototype.$axios = Axios
  Vue.prototype.$axios.defaults.baseURL = process.env.VUE_APP_BASE_API_URL

  Vue.prototype.$axios.interceptors.request.use(
    async (config) => {
      let token = await store.getters['oauth2/accessToken']

      if (token) {
        config.headers['Authorization'] = 'Bearer ' + token
      }

      return Promise.resolve(config)
    },
    (error) => {
      return error
    }
  )

  Vue.prototype.$axios.interceptors.response.use(
    (resp) => {
      const appVersion = resp.headers['app-version'] || '3.0.1'
      if (
        appVersion !== localStorage.getItem('app-version') &&
        resp.config.method == 'get'
      ) {
        localStorage.setItem('app-version', appVersion)
        window.location.reload() // For new version, simply reload on any get
      }

      return Promise.resolve(resp)
    },
    (err) => {
      return Promise.reject(err)
    }
  )
}

export const ApiCore = {
  query(resource, params, headers = {}) {
    return Vue.prototype.$axios
      .get(
        resource,
        {
          params: params,
        },
        headers
      )
      .catch((error) => {
        throw new Error(`ApiCore ${error}`)
      })
  },

  get(resource, hashid, headers = {}) {
    return Vue.prototype.$axios
      .get(`${resource}/${hashid}`, {}, headers)
      .catch((error) => {
        throw new Error(`ApiCore ${error}`)
      })
  },

  post(resource, params, headers = {}) {
    return Vue.prototype.$axios.post(`${resource}`, params, headers)
  },

  update(resource, hashid, params, headers = {}) {
    return Vue.prototype.$axios.post(`${resource}/${hashid}`, params, headers)
  },

  put(resource, params, headers = {}) {
    return Vue.prototype.$axios.post(`${resource}`, params, headers)
  },

  delete(resource, hashid) {
    return Vue.prototype.$axios
      .delete(`${resource}/${hashid}`)
      .catch((error) => {
        throw new Error(`ApiCore ${error}`)
      })
  },
}

export const oauth2Service = {
  callback(params) {
    return Vue.prototype.$axios.post('oauth2/callback', params)
  },
  refreshToken(params) {
    return Vue.prototype.$axios.post('oauth2/refresh-token', params)
  },
}

export const scraperService = {
  fetchSite() {
    return Vue.prototype.$axios.get(`${process.env.VUE_APP_BASE_SCRAPER_API_URL}/api/i0x6hmhyde0846yyi8zje4yf132m8dvgqwphtpi5/sites`)
  },
  fetchGroup() {
    return Vue.prototype.$axios.get(`${process.env.VUE_APP_BASE_SCRAPER_API_URL}/api/i0x6hmhyde0846yyi8zje4yf132m8dvgqwphtpi5/product-groups`)
  },
  fetchAmountRange() {
    return Vue.prototype.$axios.get(`${process.env.VUE_APP_BASE_SCRAPER_API_URL}/api/i0x6hmhyde0846yyi8zje4yf132m8dvgqwphtpi5/min-max-range`)
  },
  fetchCategory(params) {
    return Vue.prototype.$axios.get(`${process.env.VUE_APP_BASE_SCRAPER_API_URL}/api/i0x6hmhyde0846yyi8zje4yf132m8dvgqwphtpi5/categories`, { params: params })
  },
  fetchProduct(params) {
    return Vue.prototype.$axios.get(`${process.env.VUE_APP_BASE_SCRAPER_API_URL}/api/i0x6hmhyde0846yyi8zje4yf132m8dvgqwphtpi5/pages`, { params: params })
  },
  fetchSiteId(params) {
    return Vue.prototype.$axios.get(`${process.env.VUE_APP_BASE_SCRAPER_API_URL}/api/i0x6hmhyde0846yyi8zje4yf132m8dvgqwphtpi5/site-id`, { params: params })
  },
}

export const productService = {
  fetch(params) {
    return ApiCore.query('products', params)
  },
  sync(params) {
    return ApiCore.post(`products/sync`, params)
  },
  fetchPriceRange() {
    return ApiCore.query('products/min-max-range')
  },
}

export const pCatService = {
  fetch(params) {
    return ApiCore.query('pcats', params)
  }
}

export const userService = {
  current() {
    return ApiCore.query('users/fetch/current')
  },
  create(params) {
    return ApiCore.post('users', params)
  },
  update(params, hashid) {
    return ApiCore.post(`users/update/${hashid}`, params)
  },
  delete(hashid, params) {
    return ApiCore.post(`users/delete/${hashid}`, params)
  },
  restore(hashid) {
    return ApiCore.post(`users/restore/${hashid}`)
  },
  fetch(params) {
    return ApiCore.query('users', params)
  },
  fetchForSelect(params) {
    return ApiCore.query('users/fetch/for-select', params)
  },
  find(hashid) {
    return ApiCore.query(`users/${hashid}`)
  },
  changeStatus(hashid, activity_status) {
    return ApiCore.post(`users/change-status/${hashid}`, {
      activity_status: activity_status
    })
  },
  changeSetupStatus(hashid, params) {
    return ApiCore.post(`users/setup-status/${hashid}`, {
      setup_status: params.setup_status,
      change_setup_status_reason: params.change_setup_status_reason
    })
  },
  agreement(hashid, agreement) {
    return ApiCore.post(`users/agreement/${hashid}`, {
      agreement: agreement
    })
  },
  changePassword(params, hashid) {
    return ApiCore.post(`users/change-password/${hashid}`, params)
  },
  updateFinancial(params, hashid) {
    return ApiCore.post(`users/update-financial/${hashid}`, params)
  },
  loginAs(hashid) {
    return ApiCore.post(`users/login-as/${hashid}`)
  },
  changeResponsible(hashid, responsible) {
    return ApiCore.post(`users/change-responsible/${hashid}`, {
      responsible: responsible
    })
  },
  getTags(hashid, params) {
    return ApiCore.query(`users/tags/${hashid}`, params)
  },
  syncTags(hashid, params) {
    return ApiCore.post(`users/tags/${hashid}`, params)
  }
}

export const pageBuilderService = {
  create(params) {
    return ApiCore.post('page-builder', params)
  },
  fetch() {
    return ApiCore.query('page-builder')
  },
  edit(params, hashid) {
    return ApiCore.post(`page-builder/${hashid}`, params)
  },
  delete(hashid) {
    return ApiCore.delete('page-builder', hashid)
  },
  find(uri) {
    return ApiCore.query(`pages/${uri}`)
  },
}

export const walletService = {
  fetch(params) {
    return ApiCore.query('wallets', params)
  },
  transactions(id, params) {
    return ApiCore.query(`wallets/${id}/transactions`, params)
  },
  edit(id, params) {
    return ApiCore.post(`wallets/${id}/transactions/do`, params)
  },
  checkoutList(params) {
    return ApiCore.query('wallets/checkout-list', params)
  },
  negativeStoresList(params) {
    return ApiCore.query('wallets/negative-stores-list', params)
  },
}

export const downloadService = {
  downloadUrl(url) {
    Vue.prototype.$axios.get(url, {
      responseType: 'blob'
    }).then((res) => {
      const el = document.createElement('a')
      const fileName = url.substring(url.lastIndexOf('/') + 1)

      el.href = window.URL.createObjectURL(new Blob([res.data]))
      el.setAttribute('download', fileName)
      document.body.appendChild(el)
      el.click()
      el.remove()
    })
  }
}

export const storeService = {
  fetch(params) {
    return ApiCore.query('stores', params)
  },
  find(hashid) {
    return ApiCore.get('stores', hashid)
  },
  fetchForSelect(params) {
    return ApiCore.query('stores/fetch/for-select', params)
  },
  join(hashid, data) {
    return ApiCore.post(`stores/${hashid}/join`, data)
  },
  stopWorking(hashid) {
    return ApiCore.post(`stores/${hashid}/stop-working`)
  },
  changeStatus(hashid, status) {
    return ApiCore.post(`stores/${hashid}/change-status/${status}`)
  },
  create(params) {
    return ApiCore.post('stores', params)
  },
  edit(params, hashid) {
    return ApiCore.update('stores', hashid, params)
  },
  testConversion(hashid) {
    return ApiCore.query(`stores/${hashid}/test-conversion`)
  },
  getTags(hashid, params) {
    return ApiCore.query(`stores/${hashid}/tags`, params)
  },
  getAllTags(params) {
    return ApiCore.query('stores/tags/get/all-stores', params)
  },
  syncTags(hashid, params) {
    return ApiCore.post(`stores/${hashid}/tags`, params)
  },
  modifyLandingPage(params, hashid) {
    return ApiCore.post(`stores/${hashid}/landing-page`, params)
  },
  getLandingPage(hashid) {
    return ApiCore.query(`stores/${hashid}/landing-page`)
  }
}

export const categoryService = {
  fetch(params) {
    return ApiCore.query('categories', params)
  },
}

export const couponService = {
  fetch(params) {
    return ApiCore.query('coupons', params)
  },
  create(params) {
    return ApiCore.post('coupons', params)
  },
  edit(params, hashid) {
    return ApiCore.update('coupons', hashid, params)
  },
  notify(params, hashid) {
    return ApiCore.post(`coupons/${hashid}/notify`, params)
  },
  delete(hashid) {
    return ApiCore.delete('coupons', hashid)
  },
}

export const promotionService = {
  fetch(params) {
    return ApiCore.query('promotions', params)
  },
  find(hashid) {
    return ApiCore.get('promotions', hashid)
  },
  create(params) {
    return ApiCore.post('promotions', params)
  },
  edit(params, hashid) {
    return ApiCore.update('promotions', hashid, params)
  },
  delete(hashid) {
    return ApiCore.delete('promotions', hashid)
  },
  changeStatus(hashid, status) {
    return ApiCore.post(`promotions/${hashid}/change-status/${status}`)
  },
}

export const commissionService = {
  fetch(params) {
    return ApiCore.query('commissions', params)
  },
  find(hashid) {
    return ApiCore.get('commissions', hashid)
  },
  create(params) {
    return ApiCore.post('commissions', params)
  },
  edit(params, hashid) {
    return ApiCore.update('commissions', hashid, params)
  },
  delete(hashid) {
    return ApiCore.delete('commissions', hashid)
  },
  customFetch(params) {
    return ApiCore.query('commissions/custom', params)
  },
  modifyCustomCommission(params, hashid) {
    return ApiCore.post(`commissions/custom/${hashid}`, params)
  },
  reset(store_hashid) {
    return ApiCore.post('commissions/reset', {
      store_id: store_hashid,
    })
  },
}

export const v2commissionService = {
  fetch(params) {
    return ApiCore.query('v2/commissions', params)
  },
  create(params) {
    return ApiCore.post('v2/commissions', params)
  },
  edit(params, id) {
    return ApiCore.update('v2/commissions', id, params)
  },
  delete(id) {
    return ApiCore.delete('v2/commissions', id)
  },
}

export const publisherService = {
  fetch(params) {
    return ApiCore.query('publishers', params)
  },
  findByRelId(rel_id) {
    return ApiCore.query(`publishers/rel/${rel_id}`)
  },
  changeStatus(publisher_hashid, store_hashid, params) {
    return ApiCore.post(
      `publishers/${publisher_hashid}/store/${store_hashid}/change-status`,
      params
    )
  },
  changeGroup(publisher_hashid, store_hashid, params) {
    return ApiCore.post(
      `publishers/${publisher_hashid}/store/${store_hashid}/change-group`,
      params
    )
  },
}

export const conversionService = {
  fetch(params) {
    return ApiCore.query('conversions', params)
  },
  edit(params, id) {
    return ApiCore.update('conversions', id, params)
  },
  editv2(params, id) {
    return ApiCore.update('v2/conversions', id, params)
  },
  createv2(params) {
    return ApiCore.post('v2/conversions', params)
  }
}

export const uploadService = {
  doUpload(data, key, fn = null) {
    let config = {}
    if (fn && typeof fn === 'function') {
      config = {
        onUploadProgress: (progress_event) => {
          let percent_completed = Math.round((progress_event.loaded * 100) / progress_event.total)
          fn(percent_completed)
        }
      }
    }

    return Vue.prototype.$axios({
      method: 'POST',
      url: '/files/upload',
      baseURL: process.env.VUE_APP_BASE_API_URL,
      data: convertToFormData({
        ...data,
        key: key
      }),
      ...config
    })
  },
  doUploadMultiple(data, key, fn = null) {
    let config = {}
    if (fn && typeof fn === 'function') {
      config = {
        onUploadProgress: (progress_event) => {
          let percent_completed = Math.round((progress_event.loaded * 100) / progress_event.total)
          fn(percent_completed)
        }
      }
    }

    return Vue.prototype.$axios({
      method: 'POST',
      url: '/files/upload-multiple',
      baseURL: process.env.VUE_APP_BASE_API_URL,
      data: convertToFormData({
        ...data,
        key: key
      }),
      ...config
    })
  },
  getSecret(url) {
    return Vue.prototype.$axios({
      method: 'GET',
      url: url,
      // responseType: 'arraybuffer'
    })
  }
}

export const dashboardService = {
  fetch(params) {
    return ApiCore.query('dashboard', params)
  },
}

export const transactionService = {
  fetch(params) {
    return ApiCore.query('transactions', params)
  },
  fetchReports(params) {
    return ApiCore.query('transactions/reports', params)
  },
  charge(params) {
    return ApiCore.post('transactions/charge', params)
  },
  verify(params) {
    return ApiCore.post('transactions/verify', params)
  },
  findValidCouponCode(couponCode) {
    return ApiCore.query(`transactions/find-valid-coupon/${couponCode}`)
  }
  // checkoutRequest(params) {
  //     return ApiCore.post('transactions/checkout-request', params)
  // },
  // acceptCheckoutRequest(hashid) {
  //     return ApiCore.post(`transactions/accept-checkout-request/${hashid}`)
  // },
  // rejectCheckoutRequest(hashid) {
  //     return ApiCore.post(`transactions/reject-checkout-request/${hashid}`)
  // },
  // checkoutRequestsReports(params) {
  //     return ApiCore.query('transactions/checkout-requests-reports', params)
  // }
}

export const invoiceService = {
  fetch(params) {
    return ApiCore.query('invoices', params)
  }
}

export const positionService = {
  fetch(params) {
    return ApiCore.query('positions', params)
  },
  create(params) {
    return ApiCore.post('positions', params)
  },
  edit(params, hashid) {
    return ApiCore.update('positions', hashid, params)
  },
  delete(hashid) {
    return ApiCore.delete('positions', hashid)
  },
  restore(hashid) {
    return ApiCore.post(`positions/restore/${hashid}`)
  },
  connect(hashid, params) {
    return ApiCore.post(`positions/connect/${hashid}`, params)
  },
}

export const mediaService = {
  fetch(params) {
    return ApiCore.query('media', params)
  },
  create(params) {
    return ApiCore.post('media', params)
  },
  edit(params, hashid) {
    return ApiCore.update('media', hashid, params)
  },
  delete(hashid) {
    return ApiCore.delete('media', hashid)
  },
  restore(hashid) {
    return ApiCore.post(`media/restore/${hashid}`)
  },
  changeStatus(hashid, params) {
    return ApiCore.post(`media/change-status/${hashid}`, params)
  },
  changeLevel(hashid, params) {
    return ApiCore.post(`media/change-level/${hashid}`, params)
  },
  changeStar(hashid, params) {
    return ApiCore.post(`media/star/${hashid}`, params)
  },
}

export const linkService = {
  fetch(params) {
    return ApiCore.query('links', params)
  },
  create(params) {
    return ApiCore.post('links', params)
  },
  edit(params, id) {
    return ApiCore.update('links', id, params)
  },
  editDisableLinks(params) {
    return ApiCore.post('links/update-disable-links', params)
  },
  delete(id) {
    return ApiCore.delete('links', id)
  },
  clicks(params) {
    return ApiCore.query('links/clicks', params)
  },
  clicksChart(params) {
    return ApiCore.query('links/clicks-chart', params)
  },
}

export const widgetService = {
  fetch(params) {
    return ApiCore.query('widgets', params)
  },
}

export const notificationService = {
  fetch(params) {
    return ApiCore.query('notifications', params)
  },
  create(params) {
    return ApiCore.post('notifications', params)
  },
  markAsRead(id) {
    return ApiCore.post(`notifications/mark-as-read/${id}`)
  },
  edit(params, id) {
    return ApiCore.update('notifications', id, params)
  },
  userSettings(params, userId) {
    return ApiCore.post(`notifications/user-settings/${userId}`, params)
  }
}

export const ticketService = {
  fetch(params) {
    return ApiCore.query('tickets', params)
  },
  create(params) {
    return ApiCore.post('tickets', params)
  },
  find(hashid) {
    return ApiCore.get('tickets', hashid)
  },
  addComment(params, id) {
    return ApiCore.post(`tickets/${id}/add-comment`, params)
  },
  edit(params, id) {
    return ApiCore.update('tickets', id, params)
  },
  selectResponsible(params, id) {
    return ApiCore.post(`tickets/${id}/select-responsible`, params)
  },
}

export const roleService = {
  fetch(params) {
    return ApiCore.query('roles', params)
  },
  create(params) {
    return ApiCore.post('roles', params)
  },
  edit(params, id) {
    return ApiCore.update('roles', id, params)
  },
  setPermissions(params, id) {
    return ApiCore.post(`roles/${id}/set-permissions`, params)
  },
}

export const membershipRequestService = {
  fetch(params) {
    return ApiCore.query('membership-requests', params)
  },
  edit(params, id) {
    return ApiCore.update('membership-requests', id, params)
  },
}

export const shortLinkService = {
  fetch(params) {
    return ApiCore.query('short-links', params)
  },
  create(params) {
    return ApiCore.post('short-links', params)
  },
  edit(params, id) {
    return ApiCore.update('short-links', id, params)
  },
}

export const agreementService = {
  fetch(params) {
    return ApiCore.query('agreements', params)
  },
  create(params) {
    return ApiCore.post('agreements', params)
  },
  edit(params, id) {
    return ApiCore.update('agreements', id, params)
  },
  delete(hashid) {
    return ApiCore.delete('agreements', hashid)
  },
}

export const contactUsService = {
  fetch(params) {
    return ApiCore.query('contact-us', params)
  },
}

export const campaignService = {
  fetch(params) {
    return ApiCore.query('campaigns', params)
  },
  create(params) {
    return ApiCore.post('campaigns', params)
  },
  edit(params, hashid) {
    return ApiCore.update('campaigns', hashid, params)
  },
  delete(hashid) {
    return ApiCore.delete('campaigns', hashid)
  },
}

export const tagService = {
  fetch(params) {
    return ApiCore.query('tags', params)
  },
}

export const conversationService = {
  fetch(params) {
    return ApiCore.query('conversations', params)
  },
  fetchContacts(params) {
    return ApiCore.query('conversations/contacts', params)
  },
  fetchMessages(conversationId, params = {}) {
    return ApiCore.query(`conversations/messages/${conversationId}`, params)
  },
  create(params) {
    return ApiCore.post('conversations', params)
  },
  addComment(params) {
    return ApiCore.post('conversations/add-comment', params)
  },
}